/**
 * Web-app contracts shared by the shell (screens/services), the game screen,
 * handwriting, audio and theme modules. Core game types come from @dropstack/core.
 */
import type { Locale, Rational, RunSummary, ThemePack } from '@dropstack/core';

// ───────────── Settings ─────────────

export type InputPreference = 'auto' | 'mc' | 'hw';

export interface AudioSettings {
  master: number; // 0–1
  music: number; // 0–1
  sfx: number; // 0–1
  muted: boolean;
}

export interface Settings {
  locale: Locale;
  audio: AudioSettings;
  /** auto = handwriting on touch/pen devices once calibrated or confirmed, otherwise multiple choice. */
  input: InputPreference;
  reducedMotion: boolean;
  largeText: boolean;
  highContrast: boolean;
  leftHanded: boolean;
  /**
   * "More time" (accessibility): blocks fall at half speed — twice the time for
   * every sum (GAME_DESIGN § 4.1). Relaxed runs count for the level but are
   * never ranked. Applies from the next run.
   */
  relaxedPace: boolean;
  leaderboardOptIn: boolean;
  /** Dev/demo: render the Apple Watch layout inside a watch frame. */
  watchPreview: boolean;
  handwritingCalibrated: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  locale: 'en',
  audio: { master: 0.8, music: 0.5, sfx: 0.7, muted: false },
  input: 'auto',
  reducedMotion: false,
  largeText: false,
  highContrast: false,
  leftHanded: false,
  relaxedPace: false,
  leaderboardOptIn: false,
  watchPreview: false,
  handwritingCalibrated: false,
};

// ───────────── Backend ─────────────

export type LeaderboardScope = 'global' | 'country' | 'province' | 'city' | 'school' | 'grade';
export type LeaderboardMetric = 'rated' | 'raw';
export type LeaderboardPeriod = 'all' | 'week';

export interface PlayerProfile {
  playerId: string;
  /** False until onboarding (grade + school + location) is completed. */
  onboarded: boolean;
  displayName: string;
  grade: number | null;
  schoolId: string | null;
  schoolName: string;
  city: string;
  province: string;
  /** ISO 3166-1 alpha-2, e.g. "ZA". */
  country: string;
  leaderboardOptIn: boolean;
  locale: Locale;
}

export interface ProfileInput {
  displayName: string;
  grade: number;
  schoolId?: string | null;
  schoolName: string;
  city: string;
  province: string;
  country: string;
  leaderboardOptIn: boolean;
  locale: Locale;
}

export interface SchoolOption {
  id: string;
  name: string;
  city: string;
  province: string;
  country: string;
}

export interface SessionTicket {
  sessionId: string;
  /** Signed session token (opaque to the client). Empty string for offline runs. */
  token: string;
  seed: number;
  startRating: number;
  referenceRating: number;
  rulesVersion: number;
  /** Client clock (Date.now()) when issued — for display only; the server uses its own clock. */
  issuedAt: number;
  /** True when the server was unreachable: the run is playable but cannot be ranked. */
  offline: boolean;
  /**
   * "More time" run: the engine runs with paceConfig(true). Always the
   * backend's answer (it replays at the pace it issued), not the request.
   */
  relaxed: boolean;
}

export interface SubmitResult {
  accepted: boolean;
  flagged: boolean;
  runId: string | null;
  raw: number;
  rated: number;
  savedRating: number;
  ranks: Partial<Record<LeaderboardScope, number>>;
  personalBest: boolean;
  /**
   * True when this was the account's first completed run: an unranked placement
   * run that sets the level but never enters a leaderboard (GAME_DESIGN § 5).
   * `ranks` is empty and `personalBest` false for placement runs.
   */
  placement: boolean;
  /**
   * A "More time" (relaxed pace) run: stored and it moves the level, but it is
   * never ranked — `ranks` is empty and `personalBest` false.
   */
  relaxed: boolean;
  reason?: string;
}

export interface LeaderboardQuery {
  metric: LeaderboardMetric;
  scope: LeaderboardScope;
  period: LeaderboardPeriod;
  /** Optional grade filter that combines with any scope (e.g. "Grade 7 in Gauteng"). */
  grade?: number | null;
  country?: string;
  province?: string;
  city?: string;
  schoolId?: string;
  limit?: number;
}

export interface LeaderboardEntry {
  rank: number;
  /**
   * Opaque, stable key for this row (e.g. an HMAC of the player id). Public
   * leaderboards never expose a child's real player id, which is also the
   * username half of their credential. Use it for list keys; use `isMe` to
   * find the caller's own row.
   */
  entryKey: string;
  displayName: string;
  grade: number;
  schoolName: string;
  city: string;
  province: string;
  country: string;
  score: number;
  levelReached: number;
  /** Day of the run, YYYY-MM-DD (never a precise timestamp on public boards). */
  at: string;
  isMe?: boolean;
}

export interface LeaderboardPage {
  entries: LeaderboardEntry[];
  me: LeaderboardEntry | null;
  total: number;
  source: 'server' | 'offline-demo';
}

export interface StartSessionOptions {
  /** "More time": twice the fall time, never ranked. Default false. */
  relaxed?: boolean;
}

export interface BackendClient {
  readonly kind: 'server' | 'offline-demo';
  /** Ensures an (anonymous until onboarded) player exists and returns it. */
  ensurePlayer(): Promise<PlayerProfile>;
  saveProfile(input: ProfileInput): Promise<PlayerProfile>;
  searchSchools(q: string, filter: { country?: string; province?: string; city?: string }): Promise<SchoolOption[]>;
  suggestCities(q: string, filter: { country: string; province?: string }): Promise<string[]>;
  /** A run ticket; `relaxed` asks for a "More time" run (the ticket says what was granted). */
  startSession(options?: StartSessionOptions): Promise<SessionTicket>;
  submitRun(ticket: SessionTicket, summary: RunSummary): Promise<SubmitResult>;
  getLeaderboard(q: LeaderboardQuery): Promise<LeaderboardPage>;
}

// ───────────── Monetization ─────────────

export type AdPlacement = 'menu-banner' | 'gameover-banner' | 'play-side-banner';
export type RewardedPlacement = 'theme-sticker';

export interface AdsService {
  /** False when ads are removed or the provider is unavailable. */
  readonly enabled: boolean;
  /** Render a static, clearly labelled banner into `el`. Returns a cleanup function. */
  showBanner(slot: AdPlacement, el: HTMLElement): () => void;
  /** Opt-in rewarded ad for cosmetic, non-run benefits only. */
  showRewarded(placement: RewardedPlacement): Promise<'completed' | 'skipped' | 'unavailable'>;
}

export type ProductId = 'remove_ads' | 'plus_monthly' | 'plus_yearly';

export interface Product {
  id: ProductId;
  kind: 'one-time' | 'subscription';
  priceLabel: string;
}

export interface Entitlements {
  adsRemoved: boolean;
  plus: boolean;
  plusExpiresAt: string | null;
}

export interface PurchaseService {
  getProducts(): Promise<Product[]>;
  /** Callers MUST pass the parental gate before calling this. */
  purchase(id: ProductId): Promise<'purchased' | 'cancelled' | 'failed'>;
  restore(): Promise<Entitlements>;
  getEntitlements(): Entitlements;
  subscribe(cb: (e: Entitlements) => void): () => void;
}

/** The Dropstack Plus extras beyond ad removal (docs/MONETIZATION.md § 3.1). */
export type PlusFeatureId = 'practicePacks' | 'bonusThemes' | 'parentReport';

/** An unranked practice pack: it runs outside POST /api/sessions and never reaches a leaderboard. */
export interface PracticePack {
  id: string;
  /** Shown as the pack's name, in the player's language. */
  title: Record<Locale, string>;
  /** Grades the pack is written for (inclusive). */
  grades: { min: number; max: number };
}

/** A progress summary for parents (never shown on a public board). */
export interface ParentReport {
  /** ISO dates (YYYY-MM-DD) the report covers. */
  from: string;
  to: string;
  runs: number;
  sumsAnswered: number;
  accuracy: number;
  highestLevelReached: number;
}

/**
 * Integration points for the Plus extras. Every method is gated on an active
 * `Entitlements.plus`: without it lists are empty and the report is null.
 * `isAvailable` says whether a feature exists in this build at all; the store
 * sheet marks unavailable ones "coming soon" rather than listing them as
 * included. Nothing here may touch a ranked run.
 */
export interface PlusFeatures {
  /** Built and shipped in this build (false = "coming soon"). */
  isAvailable(feature: PlusFeatureId): boolean;
  /** Plus is active and the feature is available. */
  isUnlocked(feature: PlusFeatureId): boolean;
  practicePacks(): Promise<PracticePack[]>;
  /** Extra cosmetic theme ids for the profile (art and music only, never a level's difficulty). */
  bonusThemes(): Promise<string[]>;
  parentReport(): Promise<ParentReport | null>;
}

// ───────────── Audio ─────────────

export type SfxName =
  | 'tap'
  | 'spawn'
  | 'correct'
  | 'layerBreak'
  | 'cleanFloor'
  | 'wrong'
  | 'brickLand'
  | 'timeout'
  | 'levelUp'
  | 'levelDown'
  | 'gameOver'
  | 'danger'
  | 'hwRecognized';

export interface AudioEngine {
  /** Must be called from a user gesture before any sound plays (autoplay policy). */
  unlock(): Promise<void>;
  setTheme(theme: ThemePack): void;
  startMusic(): void;
  stopMusic(): void;
  play(sfx: SfxName): void;
  setVolumes(v: AudioSettings): void;
  /** 0–1 tension (stack height). Adds a subtle layer; never louder than the caps. */
  setIntensity(x: number): void;
  setPaused(paused: boolean): void;
}

// ───────────── Handwriting ─────────────

export type HwChar = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '-' | '/' | '.';

export interface HwPoint {
  x: number;
  y: number;
  t: number;
}

export type HwStroke = HwPoint[];

export interface HwCandidate {
  char: HwChar;
  /** 0–1, higher is better. */
  score: number;
}

export interface HwCharResult {
  candidates: HwCandidate[];
  strokeIndices: number[];
  box: { x: number; y: number; w: number; h: number };
}

export interface HwResult {
  text: string;
  chars: HwCharResult[];
  /** 0–1 overall confidence (min over characters). */
  confidence: number;
  /** Parsed value (via core parseAnswer) or null if the text isn't a valid number. */
  value: Rational | null;
}

export interface HandwritingRecognizer {
  recognize(strokes: HwStroke[]): HwResult;
  addUserSample(char: HwChar, strokes: HwStroke[]): void;
  clearUserSamples(): void;
  userSampleCount(): number;
  exportUserSamples(): string;
  importUserSamples(json: string): void;
}

// ───────────── Game screen ─────────────

export interface GameScreenProps {
  settings: Settings;
  ticket: SessionTicket;
  audio: AudioEngine;
  recognizer: HandwritingRecognizer;
  ads: AdsService;
  /** Watch layout (compact, 2×2 choices, one-digit scribble). */
  compact: boolean;
  onGameOver(summary: RunSummary, info: { quit: boolean }): void;
  onOpenSettings?(): void;
}
